import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { apiFetchJson } from '../../apiClient';
import { useMsal } from '@azure/msal-react';

const OWNERSHIP_ICONS = { 0: '👫', 1: '👩', 2: '👨' };
const OWNERSHIP_COLORS = { 0: '#3b82f6', 1: '#ec4899', 2: '#8b5cf6' };

function ExpenseCharts({ transactions, categories, period, ownershipLabels = {} }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [expandedOwnership, setExpandedOwnership] = useState(null);
  const [expandedCardholder, setExpandedCardholder] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!period) return;
    const loadAnalytics = async () => {
      try {
        setAnalytics(await apiFetchJson(instance, `/api/Expenses/analytics?period=${period}`));
      } catch (err) {
        console.error('Failed to load expense analytics:', err);
      }
    };
    loadAnalytics();
  }, [instance, period, transactions]);

  // Use server analytics if available, fall back to basic data
  const rawCategories = analytics
    ? (Array.isArray(analytics.categoryBreakdown) ? analytics.categoryBreakdown : (analytics.categoryBreakdown?.$values || []))
    : [];
  const categoryData = rawCategories.map(c => ({
    name: c.categoryName || c.name,
    icon: c.icon || '❓',
    color: c.color || '#9ca3af',
    amount: c.amount,
    count: c.count
  }));

  const rawDaily = analytics
    ? (Array.isArray(analytics.dailyBreakdown) ? analytics.dailyBreakdown : (analytics.dailyBreakdown?.$values || []))
    : [];
  // Transform daily breakdown: backend returns { date, categories: { "Food": 123, ... }, total }
  // Chart needs { day, total, Food: 123, ... }
  const dailyData = rawDaily.map((d, i) => {
    const cats = d.categories || {};
    // Unwrap $values dictionary if needed
    const catEntries = cats.$values ? {} : cats;
    const entry = { day: i + 1, total: d.total || 0 };
    Object.entries(catEntries).forEach(([name, amount]) => { entry[name] = amount; });
    return entry;
  });

  const rawOwnership = analytics
    ? (Array.isArray(analytics.ownershipBreakdown) ? analytics.ownershipBreakdown : (analytics.ownershipBreakdown?.$values || []))
    : [];
  const ownershipBreakdown = rawOwnership.map(o => ({
    ownership: o.ownership,
    label: ownershipLabels[o.ownership] || [t('expenses.common'), 'Person 1', 'Person 2'][o.ownership],
    icon: OWNERSHIP_ICONS[o.ownership],
    color: OWNERSHIP_COLORS[o.ownership],
    total: o.amount || 0,
    count: o.count || 0,
    categories: (Array.isArray(o.categories) ? o.categories : (o.categories?.$values || []))
      .map(c => ({ name: c.categoryName || c.name, icon: c.icon || '❓', amount: c.amount, count: c.count }))
  }));

  // Cardholder breakdown — for credit card validation
  const rawCardholders = analytics
    ? (Array.isArray(analytics.cardholderBreakdown) ? analytics.cardholderBreakdown : (analytics.cardholderBreakdown?.$values || []))
    : [];
  const cardholderBreakdown = rawCardholders.map(ch => ({
    cardholder: ch.cardholder || t('expenses.unknown'),
    total: ch.amount || 0,
    count: ch.count || 0,
    categories: (Array.isArray(ch.categories) ? ch.categories : (ch.categories?.$values || []))
      .map(c => ({ name: c.categoryName || c.name, icon: c.icon || '❓', amount: c.amount, count: c.count }))
  }));

  const totalAmount = analytics?.totals?.amount || 0;
  const approvedCount = analytics?.totals?.count || transactions.filter(t => t.isApproved && !t.isHidden).length;

  // Read theme-aware colors for SVG elements (re-evaluated on re-render when theme changes)
  const styles = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const pieStroke = styles?.getPropertyValue('--surface-card-alt').trim() || '#fff';
  const labelFill = styles?.getPropertyValue('--text-secondary').trim() || '#3b434f';

  const categoryNames = categoryData.map(c => c.name);
  const categoryColorMap = {};
  categoryData.forEach(c => { categoryColorMap[c.name] = c.color; });

  if (approvedCount === 0 && categoryData.length === 0) {
    return (
      <div className="expense-charts-empty">
        <p>{t('expenses.noApprovedToVisualize')}</p>
      </div>
    );
  }

  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    const pct = totalAmount > 0 ? ((d.amount / totalAmount) * 100).toFixed(1) : 0;
    return (
      <div className="expense-chart-tooltip">
        <p className="tooltip-title">{d.icon} {d.name}</p>
        <p className="tooltip-amount">{d.amount.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</p>
        <p className="tooltip-detail">{pct}% · {d.count} {t('expenses.transactions')}</p>
      </div>
    );
  };

  const CustomStackedBarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const dayData = payload[0]?.payload;
    if (!dayData || dayData.total === 0) return null;

    // Build breakdown from the category keys on the day data
    const cats = categoryNames
      .filter(name => dayData[name] > 0)
      .map(name => ({ name, icon: categoryData.find(c => c.name === name)?.icon || '❓', amount: dayData[name] }))
      .sort((a, b) => b.amount - a.amount);

    return (
      <div className="expense-chart-tooltip stacked-tooltip">
        <p className="tooltip-title">{t('expenses.dayLabel')} {label}</p>
        <p className="tooltip-amount">{dayData.total.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</p>
        <div className="tooltip-breakdown">
          {cats.map((cat, i) => (
            <div key={i} className="tooltip-breakdown-row">
              <span className="tooltip-cat-name">{cat.icon} {cat.name}</span>
              <span className="tooltip-cat-amount">{cat.amount.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, payload }) => {
    if (totalAmount > 0 && (payload.amount / totalAmount) < 0.04) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12} fill={labelFill}>
        {payload.icon} {payload.name}
      </text>
    );
  };

  return (
    <div className="expense-charts">
      {/* Summary strip */}
      <div className="expense-summary-strip">
        <div className="expense-summary-item total">
          <span className="summary-label">{t('expenses.total')}</span>
          <span className="summary-value">{totalAmount.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
        </div>
        {ownershipBreakdown.map(o => (
          <div key={o.label} className="expense-summary-item">
            <span className="summary-label">{o.label}</span>
            <span className="summary-value">{o.total.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
          </div>
        ))}
        <div className="expense-summary-item">
          <span className="summary-label">{t('expenses.transactions')}</span>
          <span className="summary-value">{approvedCount}</span>
        </div>
      </div>

      {/* Ownership breakdown */}
      <div className="expense-ownership-section">
        {ownershipBreakdown.map(group => (
          <div key={group.ownership} className="expense-ownership-card" style={{ borderColor: group.color }}>
            <button
              className="expense-ownership-header"
              onClick={() => setExpandedOwnership(expandedOwnership === group.ownership ? null : group.ownership)}
            >
              <div className="ownership-header-left">
                <span className="ownership-icon">{group.icon}</span>
                <span className="ownership-label">{group.label}</span>
                <span className="ownership-count">{group.count} {t('expenses.txns')}</span>
              </div>
              <div className="ownership-header-right">
                <span className="ownership-total" style={{ color: group.color }}>
                  {group.total.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr
                </span>
                <span className="ownership-chevron">{expandedOwnership === group.ownership ? '▼' : '►'}</span>
              </div>
            </button>
            {expandedOwnership === group.ownership && group.categories.length > 0 && (
              <div className="expense-ownership-details">
                {group.categories.map((cat, i) => (
                  <div key={i} className="ownership-detail-row">
                    <span>{cat.icon} {cat.name} <span className="ownership-detail-count">({cat.count})</span></span>
                    <span className="ownership-detail-amount">{cat.amount.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Cardholder breakdown — credit card validation */}
      {cardholderBreakdown.length > 0 && (
        <div className="expense-cardholder-breakdown">
          <h3 className="expense-section-heading">💳 {t('expenses.spendingPerPerson')}</h3>
          <div className="expense-cardholder-cards">
            {cardholderBreakdown.map((ch, idx) => {
              const pct = totalAmount > 0 ? ((ch.total / totalAmount) * 100).toFixed(1) : 0;
              return (
                <div key={idx} className="expense-cardholder-card">
                  <button
                    className="expense-cardholder-card-header"
                    onClick={() => setExpandedCardholder(expandedCardholder === idx ? null : idx)}
                  >
                    <div className="cardholder-header-left">
                      <span className="cardholder-name">💳 {ch.cardholder}</span>
                      <span className="cardholder-count">{ch.count} {t('expenses.txns')} · {pct}%</span>
                    </div>
                    <div className="cardholder-header-right">
                      <span className="cardholder-total">{ch.total.toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr</span>
                      <span className="ownership-chevron">{expandedCardholder === idx ? '▼' : '►'}</span>
                    </div>
                  </button>
                  {/* Progress bar showing share of total */}
                  <div className="cardholder-bar-track">
                    <div className="cardholder-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  {expandedCardholder === idx && ch.categories.length > 0 && (
                    <div className="expense-ownership-details">
                      {ch.categories.map((cat, i) => (
                        <div key={i} className="ownership-detail-row">
                          <span>{cat.icon} {cat.name} <span className="ownership-detail-count">({cat.count})</span></span>
                          <span className="ownership-detail-amount">{cat.amount.toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts grid */}
      <div className="expense-charts-grid">
        {/* Pie chart */}
        <div className="expense-chart-card">
          <h3>{t('expenses.spendingByCategory')}</h3>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={categoryData}
                dataKey="amount"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={120}
                innerRadius={60}
                paddingAngle={2}
                label={renderPieLabel}
              >
                {categoryData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} stroke={pieStroke} strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          {/* Legend below */}
          <div className="expense-pie-legend">
            {categoryData.map((d, i) => (
              <span key={i} className="pie-legend-item">
                <span className="pie-legend-dot" style={{ background: d.color }} />
                {d.icon} {d.name}: {d.amount.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr
              </span>
            ))}
          </div>
        </div>

        {/* Stacked bar chart */}
        <div className="expense-chart-card">
          <h3>{t('expenses.dailySpending')}</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={dailyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomStackedBarTooltip />} />
              {categoryNames.map(name => (
                <Bar
                  key={name}
                  dataKey={name}
                  stackId="daily"
                  fill={categoryColorMap[name]}
                  maxBarSize={30}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default ExpenseCharts;
